import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orgsAPI, billingAPI, profileAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import { Loader2, CheckCircle2, ArrowLeft, ArrowRight } from 'lucide-react';
import SkillDeckLogo from '../components/SkillDeckLogo';

const PLANS = [
  {
    code: 'starter',
    name: 'Starter',
    price: '0 €',
    period: '/mois',
    description: 'Pour découvrir la plateforme',
    features: ['1 utilisateur', '4 dossiers / mois', 'Upload PDF & Word', 'Export PDF', '1 template inclus'],
  },
  {
    code: 'pro',
    name: 'Pro',
    price: '49 €',
    period: '/mois',
    description: 'Pour les indépendants et consultants solo',
    popular: true,
    features: ['1 utilisateur', '50 dossiers / mois', 'Export PDF, PPT & lien partageable', 'Bibliothèque de templates', 'Branding personnalisé'],
  },
  {
    code: 'team',
    name: 'Team',
    price: '199 €',
    period: '/mois',
    description: 'Pour les équipes et ESN',
    features: ['5 utilisateurs inclus', 'Dossiers illimités', 'Tous les exports', 'Templates partagés', 'Tableau de bord équipe'],
  },
  {
    code: 'enterprise',
    name: 'Enterprise',
    price: 'Sur mesure',
    period: '',
    description: 'Pour les grandes organisations',
    features: ['Utilisateurs illimités', 'Intégration ATS / CRM', 'SSO / SAML', 'Account manager dédié'],
  },
];

const StepDot = ({ active, completed, label }) => (
  <div
    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
      active || completed ? 'bg-violet-600 text-white' : 'bg-[#1a1a2e] text-gray-500 border border-[rgba(108,71,255,.25)]'
    }`}
  >
    {completed ? <CheckCircle2 className="h-4 w-4" /> : label}
  </div>
);

const FieldWithError = ({ label, id, error, children }) => (
  <div className="space-y-1">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const Register = () => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Step 2 — professional profile
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [yearsOfExp, setYearsOfExp] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { register, refreshOrg } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { document.title = 'Skilldeck — Inscription'; }, []);

  const toSlug = (str) =>
    str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 50);

  const validateStep1 = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Le nom est requis';
    if (!orgName.trim()) newErrors.orgName = "Le nom de l'organisation est requis";
    if (!email.trim()) newErrors.email = "L'email est requis";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email invalide';
    if (password.length < 6) newErrors.password = 'Minimum 6 caractères';
    if (password !== confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Next = (e) => {
    e.preventDefault();
    if (validateStep1()) setStep(2);
  };

  const handleStep2Next = () => setStep(3);
  const handleSkipStep2 = () => setStep(3);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const result = await register(email, password, name);
      if (result.success) {
        if (result.user) {
          try {
            const slug = toSlug(orgName) || `org-${Date.now()}`;
            await orgsAPI.create({ name: orgName.trim(), slug });
            await refreshOrg();
          } catch (orgErr) {
            console.error('Org creation failed:', orgErr);
          }
          // Save professional profile (best-effort, no need to block)
          try {
            const profilePayload = {};
            if (phone.trim()) profilePayload.phone = phone.trim();
            if (jobTitle.trim()) profilePayload.job_title = jobTitle.trim();
            if (yearsOfExp) profilePayload.years_of_experience = parseInt(yearsOfExp, 10);
            if (Object.keys(profilePayload).length > 0) {
              await profileAPI.update(profilePayload);
            }
          } catch (profileErr) {
            console.error('Profile update failed:', profileErr);
          }
        }

        if (selectedPlan === 'pro' || selectedPlan === 'team') {
          try {
            await refreshOrg();
            const { checkout_url } = await billingAPI.createCheckout(selectedPlan);
            toast({ title: 'Redirection vers le paiement…', description: 'Vous allez être redirigé vers Stripe.' });
            window.location.href = checkout_url;
            return;
          } catch (err) {
            console.error('Checkout redirect failed:', err);
          }
        }

        toast({ title: 'Inscription réussie !', description: 'Bienvenue sur Skilldeck' });
        navigate('/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message || "Une erreur est survenue lors de l'inscription",
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#05050f] p-4">
      <div className={`w-full ${step === 3 ? 'max-w-5xl' : 'max-w-md'} transition-all duration-300`}>

        {/* Step indicator — 3 steps */}
        <div className="flex items-center justify-center mb-6 space-x-3">
          <StepDot active={step === 1} completed={step > 1} label="1" />
          <div className={`w-12 h-0.5 ${step > 1 ? 'bg-violet-600' : 'bg-gray-700'}`} />
          <StepDot active={step === 2} completed={step > 2} label="2" />
          <div className={`w-12 h-0.5 ${step > 2 ? 'bg-violet-600' : 'bg-gray-700'}`} />
          <StepDot active={step === 3} completed={false} label="3" />
        </div>

        {step === 1 && (
          <Card className="shadow-xl border-[rgba(108,71,255,.25)]">
            <CardHeader className="space-y-2 text-center">
              <div className="flex justify-center mb-4">
                <SkillDeckLogo size={64} />
              </div>
              <CardTitle className="text-2xl font-bold text-white">Créer votre compte</CardTitle>
              <CardDescription>Commencez à gérer vos dossiers de compétences</CardDescription>
            </CardHeader>
            <form onSubmit={handleStep1Next}>
              <CardContent className="space-y-4">
                <FieldWithError label="Nom complet" id="name" error={errors.name}>
                  <Input id="name" placeholder="Jean Dupont" value={name} onChange={(e) => setName(e.target.value)} className={errors.name ? 'border-red-500' : ''} />
                </FieldWithError>
                <FieldWithError label="Nom de l'organisation" id="orgName" error={errors.orgName}>
                  <Input id="orgName" placeholder="Ma Société SAS" value={orgName} onChange={(e) => setOrgName(e.target.value)} className={errors.orgName ? 'border-red-500' : ''} />
                </FieldWithError>
                <FieldWithError label="Email" id="email" error={errors.email}>
                  <Input id="email" type="email" placeholder="votre@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className={errors.email ? 'border-red-500' : ''} />
                </FieldWithError>
                <FieldWithError label="Mot de passe" id="password" error={errors.password}>
                  <Input id="password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className={errors.password ? 'border-red-500' : ''} />
                </FieldWithError>
                <FieldWithError label="Confirmer le mot de passe" id="confirmPassword" error={errors.confirmPassword}>
                  <Input id="confirmPassword" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={errors.confirmPassword ? 'border-red-500' : ''} />
                </FieldWithError>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4">
                <Button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700">
                  Continuer
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-sm text-center text-muted-foreground">
                  Déjà un compte ?{' '}
                  <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium">Se connecter</Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        )}

        {step === 2 && (          <Card className="shadow-xl border-[rgba(108,71,255,.25)]">
            <CardHeader className="space-y-2 text-center">
              <CardTitle className="text-xl font-bold text-white">Votre profil professionnel</CardTitle>
              <CardDescription>Ces informations apparaîtront dans vos dossiers PDF générés</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+33 6 00 00 00 00"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="jobTitle">Titre / poste</Label>
                <Input
                  id="jobTitle"
                  placeholder="Consultant Senior"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="yearsOfExp">Années d'expérience</Label>
                <Input
                  id="yearsOfExp"
                  type="number"
                  min={0}
                  max={50}
                  placeholder="5"
                  value={yearsOfExp}
                  onChange={(e) => setYearsOfExp(e.target.value)}
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-3">
              <Button
                className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700"
                onClick={handleStep2Next}
              >
                Choisir un plan
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <div className="flex items-center justify-between w-full">
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                  <ArrowLeft className="mr-1 h-3 w-3" />
                  Retour
                </Button>
                <button
                  type="button"
                  onClick={handleSkipStep2}
                  className="text-xs text-gray-500 hover:text-gray-300 underline"
                >
                  Passer cette étape
                </button>
              </div>
            </CardFooter>
          </Card>
        )}

        {step === 3 && (          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Choisissez votre plan</h2>
              <p className="text-gray-400 mt-2">Vous pourrez changer à tout moment</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {PLANS.map((plan) => (
                <Card
                  key={plan.code}
                  onClick={() => plan.code !== 'enterprise' && setSelectedPlan(plan.code)}
                  className={`relative cursor-pointer transition-all duration-200 ${
                    plan.code === 'enterprise' ? 'opacity-75 cursor-default' : ''
                  } ${
                    selectedPlan === plan.code
                      ? 'border-2 border-violet-600 shadow-lg shadow-violet-900/30 ring-2 ring-violet-600/20'
                      : 'border border-[rgba(108,71,255,.2)] hover:border-[rgba(108,71,255,.4)] hover:shadow-md'
                  } ${plan.popular ? 'scale-[1.02]' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-violet-600 to-blue-600 text-white text-xs font-bold rounded-full">
                      Populaire
                    </div>
                  )}
                  <CardContent className="pt-6 pb-6 px-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-white">{plan.name}</h3>
                      {selectedPlan === plan.code && <CheckCircle2 className="h-5 w-5 text-violet-600" />}
                    </div>
                    <div className="flex items-baseline mb-1">
                      <span className="text-2xl font-extrabold text-white">{plan.price}</span>
                      {plan.period && <span className="text-gray-400 text-sm ml-1">{plan.period}</span>}
                    </div>
                    <p className="text-xs text-gray-500 mb-4">{plan.description}</p>
                    <ul className="space-y-2">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center text-xs text-gray-300">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-400 mr-2 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {plan.code === 'enterprise' && (
                      <p className="text-xs text-gray-500 mt-4 text-center">Contactez-nous après inscription</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex items-center justify-between mt-8">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 px-8"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Création en cours…</>
                ) : (
                  (selectedPlan === 'pro' || selectedPlan === 'team') ? 'Créer le compte & payer' : 'Créer mon compte'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;

